import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import './GraphSidePanel.css';

const GraphSidePanel = ({ 
  isOpen, 
  onClose, 
  type, // 'node-edit', 'node-create', 'edge-edit', 'edge-create'
  data, // Les données à éditer (node ou edge)
  onSave,
  onDelete,
  availableNodes = [], // Pour la création d'edges
  onSelectNode // Callback pour sélectionner un nœud après création
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isInFullscreen, setIsInFullscreen] = useState(false);

  // Initialize form data based on type and data
  useEffect(() => {
    if (type === 'node-edit' && data) {
      setFormData({
        name: data.name || '',
        description: data.description || '',
        type: data.type || '',
        technology: data.technology || '',
        status: data.status || '',
        metadata: data.metadata || {},
        endpointSchema: data.endpointSchema ? JSON.stringify(data.endpointSchema, null, 2) : '{}'
      });
    } else if (type === 'node-create') {
      setFormData({
        name: '',
        description: '',
        type: 'Feature',
        technology: '',
        status: 'Planning',
        metadata: {},
        endpointSchema: '{}'
      });
    } else if (type === 'edge-edit' && data) {
      setFormData({
        from: data.from || '',
        to: data.to || '',
        relationshipType: data.relationshipType || '',
        properties: data.properties || {}
      });
    } else if (type === 'edge-create') {
      setFormData({
        from: '',
        to: '',
        relationshipType: 'DEPENDS_ON',
        properties: {}
      });
    }
    setErrors({});
  }, [type, data, isOpen]);

  // Listen to fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsInFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (type.includes('node')) {
      if (!formData.name?.trim()) newErrors.name = 'Le nom est requis';
      if (!formData.type?.trim()) newErrors.type = 'Le type est requis';
    } else if (type.includes('edge')) {
      if (!formData.from?.trim()) newErrors.from = 'Le nœud source est requis';
      if (!formData.to?.trim()) newErrors.to = 'Le nœud cible est requis';
      if (!formData.relationshipType?.trim()) newErrors.relationshipType = 'Le type de relation est requis';
      if (formData.from === formData.to) newErrors.to = t('graphSidePanel.errorSameNodes');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (type === 'node-create') {
        // Parse endpointSchema JSON for node operations
        let endpointSchemaObj = {};
        if (formData.endpointSchema) {
          try {
            endpointSchemaObj = JSON.parse(formData.endpointSchema);
          } catch (error) {
            setErrors({ endpointSchema: 'Le schéma des endpoints doit être du JSON valide' });
            setLoading(false);
            return;
          }
        }
        
        const nodeData = {
          ...formData,
          endpointSchema: endpointSchemaObj
        };
        const response = await apiClient.post('/node', nodeData);
        
        // Set the newly created node as selected and refresh graph
        if (onSelectNode) {
          onSelectNode(response.data || { name: formData.name, ...nodeData });
        }
        
        // Save the selected node to localStorage
        if (response.data) {
          localStorage.setItem('selectedNode', JSON.stringify(response.data));
        } else {
          localStorage.setItem('selectedNode', JSON.stringify({ name: formData.name, ...nodeData }));
        }
        
        // Save that we should show FeatureManager after refresh
        localStorage.setItem('showFeatureManagerAfterRefresh', 'true');
        
        // Reload the page to refresh all data and show FeatureManager
        window.location.reload();
      } else if (type === 'node-edit') {
        // Parse endpointSchema JSON for node operations
        let endpointSchemaObj = {};
        if (formData.endpointSchema) {
          try {
            endpointSchemaObj = JSON.parse(formData.endpointSchema);
          } catch (error) {
            setErrors({ endpointSchema: 'Le schéma des endpoints doit être du JSON valide' });
            setLoading(false);
            return;
          }
        }
        
        const nodeData = {
          newName: formData.name, // Utiliser newName pour le changement de nom
          description: formData.description,
          type: formData.type,
          technology: formData.technology,
          status: formData.status,
          metadata: formData.metadata || {},
          endpointSchema: endpointSchemaObj
        };
        await apiClient.put(`/node/${data.name}`, nodeData);
      } else if (type === 'edge-create') {
        await apiClient.post('/node/relationship', {
          nodeName1: formData.from,
          nodeName2: formData.to,
          relationshipType: formData.relationshipType
        });
      } else if (type === 'edge-edit') {
        // Logic for editing edges: DELETE old + POST new
        // Only update if something changed
        if (data.from !== formData.from || data.to !== formData.to || data.relationshipType !== formData.relationshipType) {
          // Delete the old relationship
          await apiClient.delete('/node/relationship', {
            data: {
              nodeName1: data.from,
              nodeName2: data.to,
              relationshipType: data.relationshipType
            }
          });
          // Create the new relationship
          await apiClient.post('/node/relationship', {
            nodeName1: formData.from,
            nodeName2: formData.to,
            relationshipType: formData.relationshipType
          });
        }
      }
      
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setErrors({ general: 'Erreur lors de la sauvegarde. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) return;
    
    setLoading(true);
    try {
      if (type === 'node-edit') {
        await apiClient.delete(`/node/${data.name}`);
        // Clear selected node from localStorage when deleting a node
        localStorage.removeItem('selectedNode');
        // Save that we should show FeatureManager after refresh
        localStorage.setItem('showFeatureManagerAfterRefresh', 'true');
        // Reload the page to ensure a clean lab state
        window.location.reload();
      } else if (type === 'edge-edit') {
        await apiClient.delete(`/relationship/${data.from}/${data.to}/${data.relationshipType}`);
        // Save that we should show FeatureManager after refresh
        localStorage.setItem('showFeatureManagerAfterRefresh', 'true');
        // Reload the page to ensure a clean lab state
        window.location.reload();
      }
      
      onDelete?.();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setErrors({ general: 'Erreur lors de la suppression. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'node-create': return t('graphSidePanel.createNode');
      case 'node-edit': return t('graphSidePanel.editNodeName', { name: data?.name || t('graphSidePanel.theNode') });
      case 'edge-create': return t('graphSidePanel.createRelation');
      case 'edge-edit': return t('graphSidePanel.editRelation');
      default: return t('graphSidePanel.edit');
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'node-create': return '➕';
      case 'node-edit': return '✏️';
      case 'edge-create': return '🔗';
      case 'edge-edit': return '🔧';
      default: return '📝';
    }
  };

  // Sort availableNodes alphabetically by name before rendering
  const sortedNodes = [...availableNodes].sort((a, b) => a.name.localeCompare(b.name));

  if (!isOpen) return null;

  // Find the fullscreen element or fallback to document.body
  const renderTarget = document.fullscreenElement || document.body;

  return ReactDOM.createPortal(
    <>
      {/* Overlay */}
      <div className={`graph-sidepanel-overlay ${isInFullscreen ? 'in-fullscreen' : ''}`} onClick={onClose} />
      
      {/* Side Panel */}
      <div className={`graph-sidepanel ${isOpen ? 'open' : ''} ${isInFullscreen ? 'in-fullscreen' : ''}`}>
        {/* Header */}
        <div className="graph-sidepanel-header">
          <div className="graph-sidepanel-title">
            <span className="graph-sidepanel-icon">{getIcon()}</span>
            <h3>{getTitle()}</h3>
          </div>
          <button className="graph-sidepanel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="graph-sidepanel-content">
          {errors.general && (
            <div className="graph-sidepanel-error">
              {errors.general}
            </div>
          )}

          {/* Node Forms */}
          {type.includes('node') && (
            <div className="graph-sidepanel-form">
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'error' : ''}
                  placeholder="Nom du nœud"
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label>Type *</label>
                <select
                  value={formData.type || ''}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className={errors.type ? 'error' : ''}
                >
                  <option value="">Sélectionner un type</option>
                  <option value="Feature">Feature</option>
                  <option value="Module">Module</option>
                  <option value="API">API</option>
                  <option value="Database">Database</option>
                  <option value="Service">Service</option>
                  <option value="User">User</option>
                </select>
                {errors.type && <span className="field-error">{errors.type}</span>}
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Description détaillée"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Technologie</label>
                <input
                  type="text"
                  value={formData.technology || ''}
                  onChange={(e) => handleInputChange('technology', e.target.value)}
                  placeholder="ex: React, Node.js, PostgreSQL"
                />
              </div>

              <div className="form-group">
                <label>Statut</label>
                <select
                  value={formData.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="Planning">Planning</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Testing">Testing</option>
                  <option value="Done">Done</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>

              <div className="form-group">
                <label>Schéma des Endpoints (JSON)</label>
                <textarea
                  value={formData.endpointSchema || '{}'}
                  onChange={(e) => handleInputChange('endpointSchema', e.target.value)}
                  placeholder='{"paths": {}, "definitions": {}}'
                  rows="4"
                  className={errors.endpointSchema ? 'error' : ''}
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
                {errors.endpointSchema && <span className="field-error">{errors.endpointSchema}</span>}
                <small className="field-hint">Format JSON pour définir les endpoints de l'API</small>
              </div>
            </div>
          )}

          {/* Edge Forms */}
          {type.includes('edge') && (
            <div className="graph-sidepanel-form">
              <div className="form-group">
                <label>Nœud source *</label>
                <select
                  value={formData.from || ''}
                  onChange={(e) => handleInputChange('from', e.target.value)}
                  className={errors.from ? 'error' : ''}
                  disabled={type === 'edge-edit'}
                >
                  <option value="">Sélectionner le nœud source</option>
                  {sortedNodes.map(node => (
                    <option key={node.name} value={node.name}>
                      {node.name} ({node.type})
                    </option>
                  ))}
                </select>
                {errors.from && <span className="field-error">{errors.from}</span>}
              </div>

              <div className="form-group">
                <label>Nœud cible *</label>
                <select
                  value={formData.to || ''}
                  onChange={(e) => handleInputChange('to', e.target.value)}
                  className={errors.to ? 'error' : ''}
                  disabled={type === 'edge-edit'}
                >
                  <option value="">Sélectionner le nœud cible</option>
                  {sortedNodes.map(node => (
                    <option key={node.name} value={node.name}>
                      {node.name} ({node.type})
                    </option>
                  ))}
                </select>
                {errors.to && <span className="field-error">{errors.to}</span>}
              </div>

              <div className="form-group">
                <label>Type de relation *</label>
                <select
                  value={formData.relationshipType || ''}
                  onChange={(e) => handleInputChange('relationshipType', e.target.value)}
                  className={errors.relationshipType ? 'error' : ''}
                >
                  <option value="">Sélectionner le type</option>
                  <option value="DEPENDS_ON">DEPENDS_ON</option>
                  <option value="USES">USES</option>
                  <option value="IMPLEMENTS">IMPLEMENTS</option>
                  <option value="CALLS">CALLS</option>
                  <option value="CONTAINS">CONTAINS</option>
                  <option value="EXTENDS">EXTENDS</option>
                  <option value="CONFIGURES">CONFIGURES</option>
                </select>
                {errors.relationshipType && <span className="field-error">{errors.relationshipType}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="graph-sidepanel-footer">
          <div className="footer-actions">
            {(type.includes('edit')) && (
              <button
                className="btn-delete_Side"
                onClick={handleDelete}
                disabled={loading}
              >
                🗑️ Supprimer
              </button>
            )}
            
            <div className="footer-main-actions">
              <button
                className="btn-cancel"
                onClick={onClose}
                disabled={loading}
              >
                {t('actions.cancel')}
              </button>
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? '⏳' : '💾'} {type.includes('create') ? t('actions.create') : t('actions.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    renderTarget
  );
};

export default GraphSidePanel;
